import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import img from "./all_bg.jpg";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "trade_journal";

const TradeJournal = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [formData, setFormData] = useState({
    year: null,
    title: "",
    journalName: "",
    doi: "",
    publisher: "",
    country: "",
    total_authors: "",
    author_position: "",
    is_collaborative: "",
    collaborative_authors: "",
  });
  const [recordId, setRecordId] = useState(null);
  const [showCollaboratorInput, setShowCollaboratorInput] = useState(false);

  const fieldMap = {
    year: "year",
    title: "title",
    journalName: "journal_name",
    doi: "doi",
    publisher: "publisher",
    country: "country",
    total_authors: "total_authors",
    author_position: "author_position",
    is_collaborative: "is_collaborative",
    collaborative_authors: "collaborative_authors",
  };

  const inputStyle = {
    padding: "10px",
    marginTop: "5px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    width: "100%",
    fontSize: "16px",
  };

  const fetchRecords = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
      method: "GET",
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to fetch records");

    const data = await res.json();

    const processed = (Array.isArray(data) ? data : [])
      .map((rec) => ({
        ...rec,
        year: rec.year ? new Date(rec.year) : null,
      }))
      .sort((a, b) => {
        // Put records with no date at the bottom
        if (!a.year && !b.year) return 0;
        if (!a.year) return 1;
        if (!b.year) return -1;

        // Latest year/month first
        return b.year.getTime() - a.year.getTime();
      });

    setRecords(processed);
  } catch (err) {
    toast.error(`Fetch error: ${err.message}`);
  }
};

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "is_collaborative" && value !== "Yes"
        ? { collaborative_authors: "" }
        : {}),
    }));
    if (name === "is_collaborative") {
      setShowCollaboratorInput(value === "Yes");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {};
    for (const [frontendKey, backendKey] of Object.entries(fieldMap)) {
      payload[backendKey] =
        frontendKey === "year" && formData.year
          ? format(formData.year, "yyyy-MM") + "-01"
          : formData[frontendKey];
    }

    try {
      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;
      const method = recordId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save record");

      toast.success(recordId ? "Record updated!" : "Record added!");
      setFormData({
        year: null,
        title: "",
        journalName: "",
        doi: "",
        publisher: "",
        country: "",
        total_authors: "",
        author_position: "",
        is_collaborative: "",
        collaborative_authors: "",
      });
      setRecordId(null);
      setShowCollaboratorInput(false);
      fetchRecords();
    } catch (err) {
      toast.error(`Submit error: ${err.message}`);
    }
  };

  const handleEdit = (rec) => {
    setFormData({
      year: rec.year ? new Date(rec.year) : null,
      title: rec.title || "",
      journalName: rec.journal_name || "",
      doi: rec.doi || "",
      publisher: rec.publisher || "",
      country: rec.country || "",
      total_authors: rec.total_authors || "",
      author_position: rec.author_position || "",
      is_collaborative: rec.is_collaborative || "",
      collaborative_authors: rec.collaborative_authors || "",
    });
    setShowCollaboratorInput(rec.is_collaborative === "Yes");
    setRecordId(rec.id);
    toast.info("Editing record...");
  };

  const handleDelete = (rec) => {
    const toastId = toast.info(
      <div>
        Delete this record?
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${rec.id}`,
                { method: "DELETE", credentials: "include" }
              );
              if (!res.ok) throw new Error("Failed to delete record");
              fetchRecords();
              toast.update(toastId, {
                render: "Record deleted successfully!",
                type: "success",
                autoClose: 3000,
              });
            } catch (err) {
              toast.update(toastId, {
                render: `Delete error: ${err.message}`,
                type: "error",
                autoClose: 3000,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#1e3a8a",
            marginBottom: "25px",
          }}
        >
          Trade Journal Articles
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {/* Year */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Publication Year (mm/yyyy)
              <span
                style={{
                  fontWeight: 300,
                  fontSize: "0.85em",
                  marginLeft: "6px",
                  color: "#666",
                }}
              >
                (start with latest trade journal)
              </span>
            </label>
            <DatePicker
              selected={formData.year}
              onChange={(date) =>
                setFormData((prev) => ({ ...prev, year: date }))
              }
              dateFormat="MM/yyyy"
              showMonthYearPicker
              placeholderText="Select month/year"
              style={inputStyle}
            />
          </div>

          {/* Text fields */}
          {[
            { label: "Title", name: "title" },
            { label: "Journal Name", name: "journalName" },
            { label: "DOI / Publication Link", name: "doi" },
            { label: "Publisher", name: "publisher" },
            { label: "Country", name: "country" },
          ].map((field) => (
            <div key={field.name}>
              <label style={{ fontWeight: "bold", marginTop: "10px" }}>
                {field.label}
              </label>
              <input
                type="text"
                name={field.name}
                value={formData[field.name]}
                onChange={handleChange}
                style={inputStyle}
                required
              />
            </div>
          ))}

          {/* Total Authors */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Total Number of Authors
            </label>
            <input
              type="number"
              name="total_authors"
              value={formData.total_authors}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>

          {/* Author Position */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Author Position
            </label>
            <select
              name="author_position"
              value={formData.author_position}
              onChange={handleChange}
              style={inputStyle}
              required
            >
              <option value="">Select</option>
              {["1", "2", "3", "4", "5", "Other"].map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {/* Collaborative */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Collaborative Publication with BBS Author/Colleague?
            </label>
            <select
              name="is_collaborative"
              value={formData.is_collaborative}
              onChange={handleChange}
              style={inputStyle}
              required
            >
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
            {showCollaboratorInput && (
              <input
                type="text"
                name="collaborative_authors"
                placeholder="Enter BBS author/colleague name(s)"
                value={formData.collaborative_authors}
                onChange={handleChange}
                style={{ ...inputStyle, marginTop: "5px" }}
                required
              />
            )}
          </div>

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              marginTop: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {recordId ? "Update Record" : "Add Record"}
          </button>
        </form>

        {/* Records Table */}
        {records.length > 0 && (
          <div style={{ marginTop: "30px", overflowX: "auto" }}>
            <h3 style={{ color: "#1e3a8a" }}>Your Records</h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "Title",
                    "Journal Name",
                    "DOI",
                    "Publisher",
                    "Country",
                    "Total Authors",
                    "Author Position",
                    "Is Collaborative",
                    "Collaborator Names",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        background: "#f1f5f9",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                  <th
                    style={{
                      border: "1px solid #ccc",
                      padding: "8px",
                      background: "#f1f5f9",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.title}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.journal_name}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.doi}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.publisher}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.country}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.total_authors}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.author_position}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.is_collaborative}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.collaborative_authors}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center",
                        gap: "5px",
                      }}
                    >
                      <button
                        onClick={() => handleEdit(rec)}
                        style={{
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rec)}
                        style={{
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default TradeJournal;
